import React, { useState, useEffect, useRef } from 'react';
import { BookOpen } from 'lucide-react';
import { getOptimizedImageUrl, resolveCoverUrl, getWorkerApiBase } from '../utils/imageCompressor.js';

// Module-level in-memory cache to prevent skeleton flash for already-loaded covers
const loadedCoversCache = new Set<string>();

interface LazyBookCoverProps {
  coverUrl?: string | null | any;
  title: string;
  className?: string;
  targetWidth?: number;
  quality?: number;
  hoverEffect?: boolean;
  priority?: boolean;
}

export const LazyBookCover: React.FC<LazyBookCoverProps> = ({
  coverUrl,
  title,
  className = 'w-16 h-22 rounded-lg',
  targetWidth = 300,
  quality = 75,
  hoverEffect = false,
  priority = false,
}) => {
  const resolvedUrl = resolveCoverUrl(coverUrl);
  const optimizedUrl = getOptimizedImageUrl(resolvedUrl, targetWidth, quality);

  const [activeUrl, setActiveUrl] = useState<string>(optimizedUrl);
  const isCached = Boolean(activeUrl && loadedCoversCache.has(activeUrl));
  const [isLoading, setIsLoading] = useState<boolean>(!isCached);
  const [hasError, setHasError] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Reset state when coverUrl changes
  useEffect(() => {
    const nextResolved = resolveCoverUrl(coverUrl);
    const nextUrl = getOptimizedImageUrl(nextResolved, targetWidth, quality);
    setActiveUrl(nextUrl);
    if (nextUrl && loadedCoversCache.has(nextUrl)) {
      setIsLoading(false);
      setHasError(false);
    } else {
      setIsLoading(Boolean(nextUrl));
      setHasError(false);
    }
  }, [coverUrl, targetWidth, quality]);

  // Robust check for browser-cached images where onLoad event might not fire in React
  useEffect(() => {
    let safetyTimer: any;

    if (imgRef.current) {
      if (imgRef.current.complete && activeUrl) {
        loadedCoversCache.add(activeUrl);
        setIsLoading(false);
      } else {
        // Safety timeout: if image takes too long, stop showing stuck skeleton
        safetyTimer = setTimeout(() => {
          setIsLoading(false);
        }, 2500);
      }
    }

    return () => {
      if (safetyTimer) clearTimeout(safetyTimer);
    };
  }, [activeUrl]);

  const hasValidUrl = Boolean(activeUrl && !hasError);

  const handleLoad = () => {
    if (activeUrl) loadedCoversCache.add(activeUrl);
    setIsLoading(false);
  };

  const handleError = () => {
    // Graceful proxy recovery: Try Worker proxy endpoint if not already tried
    if (activeUrl && !activeUrl.startsWith('data:') && !activeUrl.startsWith('blob:')) {
      const segments = activeUrl.split('/').filter(Boolean);
      const key = segments.length > 0 ? segments[segments.length - 1].split('?')[0] : '';
      if (key && key.includes('.')) {
        const apiBase = getWorkerApiBase();
        const proxyUrl = `${apiBase}/api/images/${encodeURIComponent(key)}`;

        if (!activeUrl.includes('/api/images/') && proxyUrl !== activeUrl) {
          setActiveUrl(proxyUrl);
          setIsLoading(true);
          setHasError(false);
          return;
        }
      }
    }
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div
      className={`relative overflow-hidden bg-slate-100 border border-slate-200/90 shadow-2xs shrink-0 select-none ${className}`}
    >
      {/* Skeleton Shimmer Loading Placeholder */}
      {hasValidUrl && isLoading && !isCached && (
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 animate-pulse flex items-center justify-center pointer-events-none">
          <BookOpen className="w-4 h-4 text-slate-300 opacity-60" />
        </div>
      )}

      {/* Main Cover Image */}
      {hasValidUrl ? (
        <img
          ref={imgRef}
          src={activeUrl}
          alt={title ? `Sampul buku: ${title}` : 'Sampul buku terdaftar UIBN'}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full object-cover ${hoverEffect ? 'group-hover:scale-105 transition-transform duration-300' : ''}`}
        />
      ) : (
        /* Fallback Placeholder UI when coverUrl is absent or breaks */
        <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center bg-gradient-to-br from-indigo-50/80 via-slate-100 to-indigo-100/40 text-indigo-600">
          <BookOpen className="w-5 h-5 mb-1 opacity-70 text-indigo-500" />
          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 line-clamp-2 leading-none">
            {title ? title.slice(0, 16) : 'UIBN'}
          </span>
        </div>
      )}
    </div>
  );
};
